import React, { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, MapPin, LogIn, LogOut, Clock, CalendarClock, History, Bell, AlertTriangle, RefreshCw, CheckCircle2, Sparkles, Flame, Camera, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";

const getPos = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error("Location is not supported by this browser."));
  navigator.geolocation.getCurrentPosition(
    (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
    (e) => reject(e),
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
  );
});

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-IN", { timeStyle: "short", timeZone: "Asia/Kolkata" }); } catch { return ""; } };
const dayName = (d) => { try { return new Date(d).toLocaleDateString("en-IN", { weekday: "long", timeZone: "Asia/Kolkata" }); } catch { return ""; } };

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const parts = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  return <span data-testid="live-clock" className="tabular-nums">{parts}</span>;
}

// Big, delightful hero for a single "today" shift with an attractive clock in/out button.
function StreakBadge({ streak }) {
  if (!streak || !streak.current) return null;
  return (
    <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-3 py-1 text-xs font-bold text-amber-100 ring-1 ring-amber-300/30" data-testid="on-time-streak">
      <Flame className="h-3.5 w-3.5 text-amber-300" />
      {streak.current}-shift on-time streak{streak.best > streak.current ? ` · best ${streak.best}` : ""}
    </div>
  );
}

function SelfieCaptureDialog({ open, onOpenChange, onConfirm, submitting }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);

  const stopCam = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  useEffect(() => {
    if (!open) { stopCam(); setShot(null); setErr(""); setReady(false); return; }
    let active = true;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } }, audio: false });
        if (!active) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) { videoRef.current.srcObject = s; await videoRef.current.play().catch(() => {}); setReady(true); }
      } catch { setErr("Camera access is required to clock in. Please allow camera access and try again."); }
    })();
    return () => { active = false; stopCam(); };
  }, [open, stopCam]);

  const capture = () => {
    const v = videoRef.current; if (!v) return;
    const size = Math.min(v.videoWidth, v.videoHeight) || 480;
    const c = document.createElement("canvas"); c.width = size; c.height = size;
    const ctx = c.getContext("2d");
    const sx = (v.videoWidth - size) / 2, sy = (v.videoHeight - size) / 2;
    ctx.translate(size, 0); ctx.scale(-1, 1);
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
    setShot(c.toDataURL("image/jpeg", 0.82));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="max-w-sm" data-testid="selfie-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="h-5 w-5 text-blue-900" /> Take a live selfie</DialogTitle>
          <DialogDescription>Location verified ✓ — now take a fresh selfie to complete attendance. Gallery and saved photos are not allowed.</DialogDescription>
        </DialogHeader>
        {err ? (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700" data-testid="selfie-error">{err}</div>
        ) : (
          <div className="relative overflow-hidden rounded-2xl bg-slate-900 aspect-square">
            {!shot ? (
              <video ref={videoRef} playsInline muted className="h-full w-full object-cover -scale-x-100" data-testid="selfie-video" />
            ) : (
              <img src={shot} alt="Live selfie preview" className="h-full w-full object-cover" data-testid="selfie-preview" />
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          {!err && !shot && <Button onClick={capture} disabled={!ready} className="w-full bg-blue-900 hover:bg-blue-950" data-testid="selfie-capture-btn"><Camera className="h-4 w-4 mr-1.5" /> Capture</Button>}
          {!err && shot && (
            <>
              <Button variant="outline" onClick={() => setShot(null)} disabled={submitting} data-testid="selfie-retake-btn"><RotateCcw className="h-4 w-4 mr-1.5" /> Retake</Button>
              <Button onClick={() => onConfirm(shot)} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="selfie-confirm-btn">{submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm</>}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Strict flow: fresh GPS -> server verify (distance + accuracy) -> live selfie -> server creates the record.
function useClockFlow(shift, onDone, { method = "portal", onSuccess, strict = true } = {}) {
  const [busy, setBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const pendingRef = useRef(null);

  const start = async (action) => {
    setBusy(true); setShowIssue(false);
    let pos;
    try { pos = await getPos(); }
    catch {
      toast.error(strict ? "Accurate location access is required to clock in." : "Location permission was denied or unavailable. Please enable location to clock in/out.");
      if (!strict) setShowIssue(true);
      setBusy(false); return;
    }
    if (!strict) {
      // QR Office Attendance — legacy direct clock, no live selfie.
      try {
        const { data } = await api.post(`/teacher/shifts/${shift.id}/${action}`, { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, method });
        toast.success(data.message); onSuccess && onSuccess(action); onDone && onDone();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not record attendance");
        if (err.response?.status === 422) setShowIssue(true);
      } finally { setBusy(false); }
      return;
    }
    try {
      await api.post(`/teacher/shifts/${shift.id}/verify-location`, { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy, action: action === "clock-in" ? "clock_in" : "clock_out" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Location check failed. Please try again.");
      if (err.response?.status === 422) setShowIssue(true);
      setBusy(false); return;
    }
    toast.success("Location verified ✓");
    pendingRef.current = { action, pos };
    setBusy(false); setSelfieOpen(true);
  };

  const confirmSelfie = async (dataUrl) => {
    const p = pendingRef.current; if (!p) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/teacher/shifts/${shift.id}/${p.action}`, { lat: p.pos.lat, lng: p.pos.lng, accuracy: p.pos.accuracy, selfie: dataUrl, method });
      toast.success(data.message);
      setSelfieOpen(false);
      onSuccess && onSuccess(p.action);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record attendance. Please retake and try again.");
    } finally { setSubmitting(false); pendingRef.current = null; }
  };

  const submitIssue = async () => {
    let pos = {}; try { pos = await getPos(); } catch { /* allow without */ }
    try { const { data } = await api.post(`/teacher/shifts/${shift.id}/issue`, { ...pos, note: "Unable to clock in — outside location or weak GPS" }); toast.success(data.message); setShowIssue(false); }
    catch { toast.error("Could not submit issue"); }
  };

  return { busy, submitting, selfieOpen, setSelfieOpen, showIssue, start, confirmSelfie, submitIssue };
}

function TodayShiftHero({ shift, onDone, streak }) {
  const [celebrating, setCelebrating] = useState(null);
  const done = shift.clock_in && shift.clock_out;
  const { busy, submitting, selfieOpen, setSelfieOpen, showIssue, start, confirmSelfie, submitIssue } = useClockFlow(shift, onDone, {
    method: "portal",
    onSuccess: (action) => { setCelebrating(action === "clock-in" ? "in" : "out"); celebrate(); setTimeout(() => setCelebrating(null), 1500); },
  });

  const chip = done
    ? { text: shift.status_text || "Shift complete", cls: "bg-emerald-400/20 text-emerald-100 ring-1 ring-emerald-300/40" }
    : shift.clock_in
      ? { text: shift.status_text || "Clocked in", cls: "bg-amber-400/20 text-amber-100 ring-1 ring-amber-300/40" }
      : { text: shift.status_text || "Not clocked in", cls: "bg-white/15 text-white ring-1 ring-white/25" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-blue-800 to-sky-600 text-white p-6 shadow-xl shadow-blue-900/25"
      data-testid={`today-hero-${shift.id}`}
    >
      <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-sky-300/10 blur-2xl" />

      <AnimatePresence>
        {celebrating && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-blue-950/55 backdrop-blur-sm rounded-3xl"
            data-testid="clock-celebration"
          >
            <motion.div initial={{ scale: 0.3, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 260, damping: 14 }}
              className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/40">
              <CheckCircle2 className="h-11 w-11 text-white" />
            </motion.div>
            <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-3 text-lg font-extrabold">
              {celebrating === "in" ? "You're clocked in! 🎉" : "Shift wrapped up! 👏"}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative flex items-start justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-sky-200 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Today's Shift</span>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${chip.cls}`} data-testid={`today-status-${shift.id}`}>{chip.text}</span>
      </div>

      <div className="relative mt-3">
        <p className="text-5xl sm:text-6xl font-extrabold leading-none tracking-tight">{<LiveClock />}</p>
        <p className="mt-2 text-sky-100 font-semibold">{dayName(shift.date)} · {formatDate(shift.date)}</p>
        <p className="mt-1 flex items-center gap-2 text-lg font-bold text-white"><Clock className="h-5 w-5 text-sky-200" /> {shift.start} – {shift.end}</p>
        {streak?.current > 0 && <div className="mt-3"><StreakBadge streak={streak} /></div>}
      </div>

      {(shift.clock_in || shift.clock_out) && (
        <div className="relative mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-sky-50">
          {shift.clock_in && <span className="flex items-center gap-1.5"><LogIn className="h-4 w-4 text-emerald-300" /> In {fmtTime(shift.clock_in.ts)}{shift.clock_in.inside === true && <span className="text-xs text-emerald-200">· verified</span>}</span>}
          {shift.clock_out && <span className="flex items-center gap-1.5"><LogOut className="h-4 w-4 text-sky-200" /> Out {fmtTime(shift.clock_out.ts)}{shift.clock_out.flagged && <span className="text-xs text-red-300">· flagged</span>}</span>}
        </div>
      )}

      <div className="relative mt-5 flex flex-wrap items-center gap-3">
        {!shift.clock_in && (
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => start("clock-in")} disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 px-7 py-3.5 text-base font-extrabold text-white shadow-lg shadow-emerald-500/40 transition-colors disabled:opacity-70"
            data-testid={`clock-in-${shift.id}`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogIn className="h-5 w-5" /> Clock In</>}
          </motion.button>
        )}
        {shift.clock_in && !shift.clock_out && (
          <motion.button whileTap={{ scale: 0.95 }} onClick={() => start("clock-out")} disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-slate-100 px-7 py-3.5 text-base font-extrabold text-indigo-900 shadow-lg shadow-black/20 transition-colors disabled:opacity-70"
            data-testid={`clock-out-${shift.id}`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><LogOut className="h-5 w-5" /> Clock Out</>}
          </motion.button>
        )}
        {done && (
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-400/20 px-5 py-3 text-base font-extrabold text-emerald-100 ring-1 ring-emerald-300/40" data-testid={`today-done-${shift.id}`}>
            <CheckCircle2 className="h-5 w-5" /> All done for today
          </span>
        )}
        <div className="[&_button]:bg-white/10 [&_button]:border-white/30 [&_button]:text-white [&_button:hover]:bg-white/20">
          <ShiftChangeDialog shift={shift} onDone={onDone} />
        </div>
        {showIssue && <Button size="sm" variant="outline" onClick={submitIssue} className="bg-amber-400/15 text-amber-100 border-amber-300/40 hover:bg-amber-400/25" data-testid={`submit-issue-${shift.id}`}><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Submit issue to admin</Button>}
      </div>
      <SelfieCaptureDialog open={selfieOpen} onOpenChange={setSelfieOpen} onConfirm={confirmSelfie} submitting={submitting} />
    </motion.div>
  );
}

function ShiftChangeDialog({ shift, onDone }) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(shift.start);
  const [end, setEnd] = useState(shift.end);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/teacher/shift-change", { shift_id: shift.id, requested_start: start, requested_end: end, reason: reason.trim() });
      toast.success(data.message); setOpen(false); onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit request"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`request-change-${shift.id}`}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Request change</Button></DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Request Shift Change</DialogTitle>
          <DialogDescription>{formatDate(shift.date)} · current {shift.start}–{shift.end}. Admin approval is required.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-sm font-semibold">New start</Label><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="change-start" /></div>
            <div><Label className="mb-1 block text-sm font-semibold">New end</Label><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="change-end" /></div>
          </div>
          <div><Label className="mb-1 block text-sm font-semibold">Reason</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="change-reason" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid="change-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ShiftCard({ shift, onDone, method = "portal", strict = true }) {
  const { busy, submitting, selfieOpen, setSelfieOpen, showIssue, start, confirmSelfie, submitIssue } = useClockFlow(shift, onDone, { method, strict });
  const done = shift.clock_in && shift.clock_out;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`shift-card-${shift.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-bold text-slate-900 flex items-center gap-2"><Clock className="h-4 w-4 text-blue-900" /> {shift.start} – {shift.end}</p>
          <p className="text-sm text-slate-500">{formatDate(shift.date)}</p>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${done ? "bg-green-100 text-green-700" : shift.clock_in ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`} data-testid={`shift-status-${shift.id}`}>{shift.status_text}</span>
      </div>
      <div className="mt-2 text-sm text-slate-600 space-y-0.5">
        {shift.clock_in && <p className="flex items-center gap-1.5 text-green-700"><CheckCircle2 className="h-4 w-4" /> In {fmtTime(shift.clock_in.ts)} {shift.clock_in.inside === true && <span className="text-xs">· Location verified</span>}</p>}
        {shift.clock_out && <p className="flex items-center gap-1.5 text-slate-700"><LogOut className="h-4 w-4" /> Out {fmtTime(shift.clock_out.ts)} {shift.clock_out.flagged && <span className="text-xs text-red-600">· flagged (outside)</span>}</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {!shift.clock_in && <Button size="sm" onClick={() => start("clock-in")} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid={`clock-in-${shift.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-1" /> Clock In</>}</Button>}
        {shift.clock_in && !shift.clock_out && <Button size="sm" onClick={() => start("clock-out")} disabled={busy} className="bg-blue-900 hover:bg-blue-950" data-testid={`clock-out-${shift.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-1" /> Clock Out</>}</Button>}
        <ShiftChangeDialog shift={shift} onDone={onDone} />
        {showIssue && <Button size="sm" variant="outline" onClick={submitIssue} className="text-amber-700 border-amber-300" data-testid={`submit-issue-${shift.id}`}><AlertTriangle className="h-3.5 w-3.5 mr-1" /> Submit issue to admin</Button>}
      </div>
      <SelfieCaptureDialog open={selfieOpen} onOpenChange={setSelfieOpen} onConfirm={confirmSelfie} submitting={submitting} />
    </div>
  );
}

export default function MyAttendance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.get("/teacher/attendance"); setData(r.data); }
    catch { toast.error("Could not load attendance"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  const d = data || {};
  const nextUp = (d.upcoming || [])[0];

  return (
    <div className="space-y-6" data-testid="my-attendance">
      <section>
        <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-blue-900" /> Today's shifts</h3>
        {(d.today || []).length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="today-empty">
            <CalendarClock className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-semibold text-slate-600">No shifts scheduled today — enjoy your day! ☕</p>
            {nextUp && <p className="mt-1 text-sm text-slate-500">Your next shift starts at <b className="text-blue-900">{nextUp.start}</b> on {formatDate(nextUp.date)}.</p>}
            {d.streak?.current > 0 && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700 ring-1 ring-amber-200" data-testid="on-time-streak-empty">
                <Flame className="h-3.5 w-3.5 text-amber-500" /> {d.streak.current}-shift on-time streak{d.streak.best > d.streak.current ? ` · best ${d.streak.best}` : ""}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="today-shifts">{d.today.map((s) => <TodayShiftHero key={s.id} shift={s} onDone={load} streak={d.streak} />)}</div>
        )}
      </section>

      <div className="rounded-2xl border border-sky-100 bg-sky-50 text-slate-700 p-4 flex items-start gap-3 text-sm" data-testid="attendance-location-note">
        <MapPin className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
        <div>
          {d.settings?.configured ? <>Location check is ON · allowed radius <b>{d.settings.radius_m} m</b>. Please allow browser location when clocking in/out.</> : <>Admin has not set the PTG location yet — attendance will be recorded without a distance check.</>}
          {nextUp && <div className="mt-0.5 text-slate-500">Your next shift starts at <b className="text-blue-900">{nextUp.start}</b> on {formatDate(nextUp.date)}.</div>}
        </div>
      </div>

      {(d.reminders || []).length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="attendance-reminders">
          <p className="font-bold text-amber-800 flex items-center gap-2 mb-2"><Bell className="h-4 w-4" /> Reminders</p>
          {d.reminders.slice(0, 5).map((r) => <p key={r.id} className="text-sm text-amber-800">• {r.message}</p>)}
        </div>
      )}

      <section>
        <h3 className="font-bold text-slate-900 mb-3">Upcoming shifts</h3>
        {(d.upcoming || []).length === 0 ? <p className="text-sm text-slate-500">No upcoming shifts.</p> :
          <div className="grid sm:grid-cols-2 gap-3" data-testid="upcoming-shifts">{d.upcoming.map((s) => <ShiftCard key={s.id} shift={s} onDone={load} />)}</div>}
      </section>

      {(d.requests || []).length > 0 && (
        <section>
          <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><RefreshCw className="h-5 w-5" /> Shift-change requests</h3>
          <div className="space-y-2" data-testid="my-change-requests">
            {d.requests.map((r) => (
              <div key={r.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm flex items-center justify-between flex-wrap gap-2">
                <span>{formatDate(r.date)}: {r.original_start}–{r.original_end} → {r.requested_start}–{r.requested_end} <span className="text-slate-500">({r.reason})</span></span>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${r.status === "approved" ? "bg-green-100 text-green-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`req-status-${r.id}`}>
                  {r.status === "approved" ? `Approved (${r.final_start}–${r.final_end})` : r.status === "rejected" ? "Rejected" : "Waiting for admin approval"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><History className="h-5 w-5" /> Attendance history</h3>
        {(d.history || []).length === 0 ? <p className="text-sm text-slate-500">No past shifts.</p> :
          <div className="space-y-2" data-testid="attendance-history">
            {d.history.map((s) => (
              <div key={s.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm flex items-center justify-between flex-wrap gap-2">
                <span>{formatDate(s.date)} · {s.start}–{s.end}</span>
                <span className="text-slate-600">{s.clock_in ? `In ${fmtTime(s.clock_in.ts)}` : "No clock-in"} {s.clock_out ? `· Out ${fmtTime(s.clock_out.ts)}` : ""} · <b>{s.status_text}</b></span>
              </div>
            ))}
          </div>}
      </section>
    </div>
  );
}
